import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Divider,
  Chip,
  TextField,
  Button,
  CircularProgress,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SearchIcon from "@mui/icons-material/Search";
import { Candidate } from "../../services/preventiviApi";
import { MatchTypeChip } from "../common/StatusChip";
import { CATEGORIES, DISCIPLINES } from "../../services/configApi";
import { formatDateLongRome } from "../../utils/dateFormat";

interface EditableFields {
  title: string;
  descrizione_breve: string;
  description: string;
  specs: string;
  codice: string;
  prezzo: string;
  costo_mat_unitario: string;
  tempo_posa_ore: string;
  costo_mano_unitario: string;
  categoria: string;
  disciplina: string;
  anno: string;
}

export interface CatalogItemChanges {
  title?: string;
  descrizione_breve?: string;
  description?: string;
  specs?: string;
  codice?: string;
  prezzo?: number | null;
  costo_mat_unitario?: number | null;
  tempo_posa_ore?: number | null;
  costo_mano_unitario?: number | null;
  categoria?: string;
  disciplina?: string;
  anno?: number | null;
}

interface CandidateDetailsModalProps {
  candidate: Candidate | null;
  open: boolean;
  onClose: () => void;
  /** When provided a pencil icon is shown and fields become editable */
  editable?: boolean;
  /** Called on save; receives only the changed fields */
  onSave?: (uid: string, changes: CatalogItemChanges) => Promise<void>;
  /** Called on delete — admin/maintainer only */
  onDelete?: (uid: string) => Promise<void>;
  /** The query text used to find this candidate (shown as context) */
  queryText?: string | null;
}

const ReadonlyField: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => {
  if (value == null || value === "") return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.2, lineHeight: 1.7 }}>{String(value)}</Typography>
    </Box>
  );
};

const numOrNull = (s: string): number | null => {
  const t = s.trim();
  if (t === "") return null;
  const n = parseFloat(t.replace(",", "."));
  return isNaN(n) ? null : n;
};

export const CandidateDetailsModal: React.FC<CandidateDetailsModalProps> = ({
  candidate,
  open,
  onClose,
  editable = false,
  onSave,
  onDelete,
  queryText,
}) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [fields, setFields] = useState<EditableFields>({
    title: "", descrizione_breve: "", description: "", specs: "",
    codice: "", prezzo: "", costo_mat_unitario: "", tempo_posa_ore: "",
    costo_mano_unitario: "", categoria: "", disciplina: "", anno: "",
  });

  const meta = candidate?.metadata ?? {};

  useEffect(() => {
    if (candidate && open) {
      setFields({
        title: meta.title ?? "",
        descrizione_breve: meta.descrizione_breve ?? "",
        description: meta.description ?? "",
        specs: meta.specs ?? "",
        codice: meta.codice ?? "",
        prezzo: meta.prezzo != null ? String(parseFloat(meta.prezzo).toFixed(2)) : "",
        costo_mat_unitario: meta.costo_mat_unitario != null ? String(parseFloat(meta.costo_mat_unitario).toFixed(2)) : "",
        tempo_posa_ore: meta.tempo_posa_ore != null ? String(parseFloat(meta.tempo_posa_ore)) : "",
        costo_mano_unitario: meta.costo_mano_unitario != null ? String(parseFloat(meta.costo_mano_unitario).toFixed(2)) : "",
        categoria: meta.categoria ?? "",
        disciplina: meta.disciplina ?? "",
        anno: meta.anno != null ? String(meta.anno) : "",
      });
      setEditing(false);
      setConfirmDeleteOpen(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate, open]);

  if (!candidate) return null;

  const set = (key: keyof EditableFields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFields((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      const changes: CatalogItemChanges = {};

      if (fields.title !== (meta.title ?? "")) changes.title = fields.title;
      if (fields.descrizione_breve !== (meta.descrizione_breve ?? "")) changes.descrizione_breve = fields.descrizione_breve;
      if (fields.description !== (meta.description ?? "")) changes.description = fields.description;
      if (fields.specs !== (meta.specs ?? "")) changes.specs = fields.specs;
      if (fields.codice !== (meta.codice ?? "")) changes.codice = fields.codice;
      if (fields.categoria !== (meta.categoria ?? "")) changes.categoria = fields.categoria;
      if (fields.disciplina !== (meta.disciplina ?? "")) changes.disciplina = fields.disciplina;

      const prezzoNum = numOrNull(fields.prezzo);
      const prezzoOrig = meta.prezzo != null ? parseFloat(meta.prezzo) : null;
      if (prezzoNum !== prezzoOrig) changes.prezzo = prezzoNum;

      const matNum = numOrNull(fields.costo_mat_unitario);
      const matOrig = meta.costo_mat_unitario != null ? parseFloat(meta.costo_mat_unitario) : null;
      if (matNum !== matOrig) changes.costo_mat_unitario = matNum;

      const posaNum = numOrNull(fields.tempo_posa_ore);
      const posaOrig = meta.tempo_posa_ore != null ? parseFloat(meta.tempo_posa_ore) : null;
      if (posaNum !== posaOrig) changes.tempo_posa_ore = posaNum;

      const manoNum = numOrNull(fields.costo_mano_unitario);
      const manoOrig = meta.costo_mano_unitario != null ? parseFloat(meta.costo_mano_unitario) : null;
      if (manoNum !== manoOrig) changes.costo_mano_unitario = manoNum;

      const annoNum = numOrNull(fields.anno);
      const annoOrig = meta.anno != null ? parseFloat(meta.anno) : null;
      if (annoNum !== annoOrig) changes.anno = annoNum;

      if (Object.keys(changes).length > 0) {
        await onSave(candidate.uid, changes);
      }
      setEditing(false);
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? "Errore nel salvataggio";
      throw new Error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || !candidate) return;
    setDeleting(true);
    try {
      await onDelete(candidate.uid);
      setConfirmDeleteOpen(false);
    } catch {
      // parent already surfaced the error; keep the confirm open for a retry
    } finally {
      setDeleting(false);
    }
  };

  // Derive a display % score: prefer match_percent, fall back to final_score * 100
  const scorePercent = candidate.match_percent != null
    ? candidate.match_percent
    : candidate.final_score < 2
      ? candidate.final_score * 100
      : candidate.final_score;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "1rem" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: 2, pr: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          {editing ? (
            <TextField
              value={fields.title}
              onChange={set("title")}
              fullWidth
              size="small"
              label="Titolo"
              disabled={saving}
            />
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
              {meta.title || candidate.uid}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1, mt: 0.75, flexWrap: "wrap", alignItems: "center" }}>
            <MatchTypeChip
              matchType={candidate.match_type}
              matchPercent={scorePercent}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {editable && !editing && (
            <Tooltip title="Modifica metadati">
              <IconButton size="small" onClick={() => setEditing(true)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Divider sx={{ mb: 2 }} />

        {/* Descrizione cercata (query context) */}
        {queryText && (
          <Box
            sx={{
              mb: 2,
              p: 1.25,
              bgcolor: "#F0F4FF",
              borderRadius: "8px",
              border: "1px solid #C7D7F9",
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
            }}
          >
            <SearchIcon sx={{ fontSize: "1rem", color: "#3D5A80", mt: 0.2, flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "#3D5A80", display: "block" }}>
                Descrizione cercata
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.1, lineHeight: 1.6, color: "#1E3A5C" }}>
                {queryText}
              </Typography>
            </Box>
          </Box>
        )}

        {candidate.reasoning && (
          <Box sx={{ mb: 2, p: 1.25, bgcolor: "rgba(0,0,0,0.03)", borderRadius: "8px", border: "1px solid rgba(0,0,0,0.07)" }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Motivazione match
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.2, lineHeight: 1.6, fontStyle: "italic" }}>
              {candidate.reasoning}
            </Typography>
          </Box>
        )}

        {editing ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Descrizione breve"
              value={fields.descrizione_breve}
              onChange={set("descrizione_breve")}
              fullWidth
              disabled={saving}
              inputProps={{ maxLength: 100 }}
              helperText={`${fields.descrizione_breve.length}/100 caratteri`}
            />
            <TextField
              label="Descrizione completa"
              value={fields.description}
              onChange={set("description")}
              fullWidth
              multiline
              rows={3}
              disabled={saving}
            />
            <TextField
              label="Specifiche tecniche"
              value={fields.specs}
              onChange={set("specs")}
              fullWidth
              multiline
              rows={2}
              disabled={saving}
            />
            <TextField
              label="Codice articolo"
              value={fields.codice}
              onChange={set("codice")}
              fullWidth
              disabled={saving}
            />

            <Divider><Typography variant="caption" color="text.disabled">Classificazione</Typography></Divider>

            <Grid container spacing={2}>
              <Grid size={6}>
                <FormControl fullWidth size="small" disabled={saving}>
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    value={fields.categoria}
                    label="Categoria"
                    onChange={(e) => setFields((f) => ({ ...f, categoria: e.target.value }))}
                  >
                    <MenuItem value=""><em>—</em></MenuItem>
                    {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={6}>
                <FormControl fullWidth size="small" disabled={saving}>
                  <InputLabel>Disciplina</InputLabel>
                  <Select
                    value={fields.disciplina}
                    label="Disciplina"
                    onChange={(e) => setFields((f) => ({ ...f, disciplina: e.target.value }))}
                  >
                    <MenuItem value=""><em>—</em></MenuItem>
                    {DISCIPLINES.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={4}>
                <TextField
                  label="Anno"
                  value={fields.anno}
                  onChange={set("anno")}
                  type="number"
                  fullWidth
                  size="small"
                  disabled={saving}
                  inputProps={{ min: 2000, max: 2099 }}
                />
              </Grid>
            </Grid>

            <Divider><Typography variant="caption" color="text.disabled">Valori economici</Typography></Divider>

            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="Costo materiale (€)"
                  value={fields.costo_mat_unitario}
                  onChange={set("costo_mat_unitario")}
                  type="number"
                  fullWidth
                  size="small"
                  disabled={saving}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Tempo posa (ore)"
                  value={fields.tempo_posa_ore}
                  onChange={set("tempo_posa_ore")}
                  type="number"
                  fullWidth
                  size="small"
                  disabled={saving}
                  inputProps={{ step: "0.0001", min: "0" }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Costo manodopera (€)"
                  value={fields.costo_mano_unitario}
                  onChange={set("costo_mano_unitario")}
                  type="number"
                  fullWidth
                  size="small"
                  disabled={saving}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="P.V. unitario (€)"
                  value={fields.prezzo}
                  onChange={set("prezzo")}
                  type="number"
                  fullWidth
                  size="small"
                  disabled={saving}
                  inputProps={{ step: "0.01", min: "0" }}
                />
              </Grid>
            </Grid>
          </Box>
        ) : (
          <>
            <ReadonlyField label="Descrizione breve" value={meta.descrizione_breve} />
            <ReadonlyField label="Descrizione completa" value={meta.description} />
            <ReadonlyField label="Specifiche tecniche" value={meta.specs} />
            <ReadonlyField label="Codice articolo" value={meta.codice} />

            {(meta.categoria || meta.disciplina || meta.anno) && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                {meta.categoria && <Chip label={meta.categoria} size="small" sx={{ fontWeight: 600 }} />}
                {meta.disciplina && <Chip label={meta.disciplina} size="small" variant="outlined" />}
                {meta.anno && <Chip label={`Anno ${meta.anno}`} size="small" variant="outlined" />}
              </Box>
            )}

            {(meta.costo_mat_unitario != null || meta.tempo_posa_ore != null || meta.costo_mano_unitario != null || meta.prezzo != null) && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 0.5 }}>
                  Valori economici
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {meta.costo_mat_unitario != null && (
                    <Typography variant="body2" sx={{ color: "#1565C0", fontWeight: 600 }}>
                      Mat: {parseFloat(meta.costo_mat_unitario).toFixed(2)} €
                    </Typography>
                  )}
                  {meta.tempo_posa_ore != null && (
                    <Typography variant="body2" sx={{ color: "#2E7D32", fontWeight: 600 }}>
                      Posa: {parseFloat(meta.tempo_posa_ore).toFixed(2)} h
                    </Typography>
                  )}
                  {meta.costo_mano_unitario != null && (
                    <Typography variant="body2" sx={{ color: "#E65100", fontWeight: 600 }}>
                      Man: {parseFloat(meta.costo_mano_unitario).toFixed(2)} €
                    </Typography>
                  )}
                  {meta.prezzo != null && (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      P.V.: {parseFloat(meta.prezzo).toFixed(2)} €
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {meta.source_file && (
            <Typography variant="caption" color="text.disabled">
              Fonte: {meta.source_file}
            </Typography>
          )}
          {meta.created_at && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: "0.9rem", color: "text.disabled" }} />
              <Typography variant="caption" color="text.secondary">
                Aggiunto il {formatDateLongRome(meta.created_at)}
                {meta.item_age_days != null && ` (${meta.item_age_days} giorni fa)`}
              </Typography>
            </Box>
          )}
          {meta.recency_penalty != null && parseFloat(meta.recency_penalty) > 0 && (
            <Chip
              label={`Recency penalty: −${(parseFloat(meta.recency_penalty) * 100).toFixed(1)}%`}
              size="small"
              sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontSize: "0.72rem" }}
            />
          )}
        </Box>

        {meta.parent_uid && (
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1 }}>
            Revisione di: {meta.parent_uid}
          </Typography>
        )}
      </DialogContent>

      {(!editing && (onDelete || editable)) && (
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
          {onDelete ? (
            <Button
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={deleting || saving}
            >
              Elimina dal catalogo
            </Button>
          ) : <Box />}
          {editable && !editing && (
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
              Modifica
            </Button>
          )}
        </DialogActions>
      )}

      {editing && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditing(false)} disabled={saving} color="inherit">
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : undefined}
          >
            Salva
          </Button>
        </DialogActions>
      )}

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => !deleting && setConfirmDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "1rem" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}>
          <DeleteOutlineIcon sx={{ color: "error.main" }} />
          Elimina articolo
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{meta.descrizione_breve || meta.title || candidate.uid}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            L'articolo verrà eliminato definitivamente dal catalogo e non sarà più proposto come
            match. L'operazione è <strong>irreversibile</strong>.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting} color="inherit">
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon />}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
